// frontend/src/pages/dashboard/PermissionsPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Typography, Box, Button, Chip, Paper, Alert, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Divider, InputAdornment, FormControl, FormLabel, Select, MenuItem,
    IconButton, Tooltip, LinearProgress, Collapse, Stack, useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Warning as WarningIcon,
    Search as SearchIcon,
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked as RadioButtonUncheckedIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { permissionAPI, roleAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Alert as MuiAlert } from '@mui/material';
import { Link } from 'react-router-dom';

const PermissionsPage = () => {
    const { user: authUser } = useAuth();    
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isAdmin = authUser?.teamMembership?.role?.name === 'system administrator';
    const [permissions, setPermissions] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Dialog states
    const [openDialog, setOpenDialog] = useState(false);
    const [currentPermission, setCurrentPermission] = useState(null);
    const [permissionForm, setPermissionForm] = useState({ name: '', display_name: '', description: '' });
    const [saving, setSaving] = useState(false);

    // UI states
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');

    // Group permissions by category
    const groupPermissions = useCallback((perms) => {
        const groups = {};

        perms.forEach(perm => {
            let category = 'Uncategorized';

            if (perm.name.startsWith('dashboard.')) category = 'Dashboard';
            else if (perm.name.startsWith('client.')) category = 'Client Management';
            else if (perm.name.startsWith('team.')) category = 'Team Management';
            else if (perm.name.startsWith('status.')) category = 'Status Management';
            else if (perm.name.startsWith('project.')) category = 'Project Management';
            else if (perm.name.startsWith('task.')) category = 'Task Management';
            else if (perm.name.startsWith('projtaskect.')) category = 'Task Management';
            else if (perm.name.startsWith('project.board')) category = 'Project Board';
            else if (perm.name.startsWith('sprint.')) category = 'Sprint Management';
            else if (perm.name.startsWith('backlog.')) category = 'Backlog Management';
            else if (perm.name.startsWith('kpi.')) category = 'KPI Management';
            else if (perm.name.startsWith('calendar.')) category = 'Calendar Management';
            else if (
                perm.name.startsWith('package.') ||
                perm.name.startsWith('billing.') ||
                perm.name.startsWith('settings.')
            ) category = 'System & Billing';

            if (!groups[category]) groups[category] = [];
            groups[category].push(perm);
        });

        // Sort categories alphabetically, but keep 'Uncategorized' last
        const orderedGroups = {};
        Object.keys(groups)
            .sort((a, b) => {
                if (a === 'Uncategorized') return 1;
                if (b === 'Uncategorized') return -1;
                return a.localeCompare(b);
            })
            .forEach(key => {
                orderedGroups[key] = groups[key];
            });

        return orderedGroups;
    }, []);

    const groupedPermissions = useMemo(() => groupPermissions(permissions), [permissions, groupPermissions]);

    // Get usage count per permission
    const getUsageCount = useCallback((permId) => {
        return roles.reduce((count, role) => {
            return count + (role.permissions?.some(p => p.id === permId) ? 1 : 0);
        }, 0);
    }, [roles]);

    // Filtered permissions
    const filteredGroups = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        const result = {};

        Object.entries(groupedPermissions).forEach(([category, perms]) => {
            if (filterCategory !== 'all' && filterCategory !== category) return;

            const filtered = perms.filter(perm =>
                (!query) ||
                perm.display_name.toLowerCase().includes(query) ||
                perm.name.toLowerCase().includes(query)
            );

            if (filtered.length > 0) {
                result[category] = filtered;
            }
        });

        return result;
    }, [groupedPermissions, searchQuery, filterCategory]);

    // Fetch permissions & roles
    const fetchPermissions = useCallback(async () => {
        try {
            const res = await permissionAPI.getAll();
            setPermissions(res.data);
        } catch {
            setError('Failed to load permissions');
        }
    }, []);

    const fetchRoles = useCallback(async () => {
        try {
            const res = await roleAPI.getAll();
            setRoles(res.data);
        } catch {
            setError('Failed to load roles');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isAdmin) return;
        fetchPermissions();
        fetchRoles();
    }, [isAdmin, fetchPermissions, fetchRoles]);

    const handleOpenDialog = (perm = null) => {
        if (perm) {
            setCurrentPermission(perm);
            setPermissionForm({
                name: perm.name,
                display_name: perm.display_name,
                description: perm.description || ''
            });
        } else {
            setCurrentPermission(null);
            setPermissionForm({ name: '', display_name: '', description: '' });
        }
        setError('');
        setSuccess('');
        setOpenDialog(true);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setPermissionForm(prev => {
            if (name === 'display_name' && !currentPermission) {
                const slug = value.toLowerCase().trim().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '_');
                return { ...prev, display_name: value, name: slug };
            }
            return { ...prev, [name]: value };
        });
    };

    const handleSubmit = async () => {
        const { name, display_name } = permissionForm;
        if (!name || !display_name) {
            setError('Name and Display Name are required');
            return;
        }
        setSaving(true);
        try {
            if (currentPermission) {
                await permissionAPI.update(currentPermission.id, permissionForm);
            } else {
                await permissionAPI.create(permissionForm);
            }
            await fetchPermissions();
            setSuccess(currentPermission ? 'Permission updated successfully' : 'Permission created successfully');
            setOpenDialog(false);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save permission');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (permId, permName) => {
        const usedInRoles = roles.filter(r =>
            r.permissions?.some(p => p.id === permId)
        );
        if (usedInRoles.length > 0) {
            if (!window.confirm(
                `Permission "${permName}" is used in ${usedInRoles.length} role(s): ${usedInRoles.map(r => r.name).join(', ')}. Delete anyway? This may break those roles.`
            )) return;
        } else if (!window.confirm(`Delete permission "${permName}"?`)) return;

        try {
            await permissionAPI.delete(permId);
            setPermissions(prev => prev.filter(p => p.id !== permId));
            setSuccess('Permission deleted successfully');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete permission');
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
                    <CircularProgress />
                </Box>
            </DashboardLayout>
        );
    }

    if (!isAdmin) {
        return (
            <DashboardLayout>
                <Box p={3}>
                    <Alert severity="error">You do not have permission to access this page.</Alert>
                </Box>
            </DashboardLayout>
        );
    }

    const categoryOptions = ['all', ...Object.keys(groupedPermissions)];

    return (
        <DashboardLayout>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h4" fontWeight={600}>Permissions</Typography>
                    <Button
                        component={Link}
                        to="/team"
                        variant="contained"
                        size="small"
                        sx={{
                            height: 48, // Slightly taller for better touch target
                            px: 3,
                            borderRadius: 3, // More rounded corners (pill-like)
                            fontWeight: 600,
                            letterSpacing: '0.5px',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            boxShadow: '0 4px 15px rgba(0,0,0,0.15)', 
                            '&:hover': {
                            boxShadow: '0 8px 25px rgba(0,0,0,0.25)', // Lift effect
                            transform: 'translateY(-1px)',
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                            },
                            '&:active': {
                            transform: 'translateY(0)',
                            },
                            width: isMobile ? "100%" : "auto",
                        }}
                    >
                        Manage Roles & Teams
                    </Button>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Granular permissions for role-based access control. Assign them on the <strong>Team</strong> page.
                </Typography>

                {/* Alerts */}
                <Collapse in={!!error}>
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {error}
                    </Alert>
                </Collapse>
                <Collapse in={!!success}>
                    <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                        {success}
                    </Alert>
                </Collapse>

                {/* Compact Controls: Search + Category Filter */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search permissions..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ flex: 1, minWidth: 200, maxWidth: 350 }}
                    />

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            displayEmpty
                            renderValue={(selected) => selected === 'all' ? 'All Categories' : selected}
                            sx={{ height: 36 }}
                        >
                            <MenuItem value="all">All Categories</MenuItem>
                            {Object.keys(groupedPermissions).map(cat => (
                                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Compact Stats Row */}
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                        label={`Total: ${permissions.length}`}
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                    />
                    <Chip
                        label={`Used: ${permissions.filter(p => getUsageCount(p.id) > 0).length}`}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                    />
                    <Chip
                        label={`Uncategorized: ${groupedPermissions['Uncategorized']?.length || 0}`}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                    />
                </Stack>

                {/* No Permissions */}
                {permissions.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                        <Typography color="text.secondary">No permissions defined yet.</Typography>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            sx={{ mt: 1 }}
                            onClick={() => handleOpenDialog()}
                        >
                            Create First Permission
                        </Button>
                    </Paper>
                ) : Object.keys(filteredGroups).length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
                        <Typography>No permissions match your search.</Typography>
                    </Paper>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {Object.entries(filteredGroups).map(([category, perms]) => (
                            <Paper
                                key={category}
                                variant="outlined"
                                sx={{
                                    borderRadius: 2,
                                    borderLeft: category === 'Uncategorized'
                                        ? '4px solid' : undefined,
                                    borderColor: category === 'Uncategorized'
                                        ? 'warning.main' : undefined,
                                    transition: 'box-shadow 0.2s',
                                    '&:hover': {
                                        boxShadow: category === 'Uncategorized' ? 1 : 2
                                    }
                                }}
                            >
                                <Box sx={{ p: 1.5, pb: 0.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="h6" fontWeight={600} fontSize="1rem">
                                            {category}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={`${perms.length} ${perms.length === 1 ? 'permission' : 'permissions'}`}
                                            color={category === 'Uncategorized' ? 'warning' : 'default'}
                                            variant="outlined"
                                        />
                                    </Box>
                                    <Divider sx={{ mb: 1 }} />
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                        {perms.map((perm) => {
                                            const usage = getUsageCount(perm.id);
                                            const isUsed = usage > 0;
                                            return (
                                                <Tooltip
                                                    key={perm.id}
                                                    title={
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600}>
                                                                {perm.name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {perm.description || 'No description'}
                                                            </Typography>
                                                            {isUsed && (
                                                                <Typography variant="caption" color="info.main" sx={{ mt: 0.5, display: 'block' }}>
                                                                    Used in {usage} {usage === 1 ? 'role' : 'roles'}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                    arrow
                                                >
                                                    <Chip
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                {perm.display_name}
                                                                {isUsed && (
                                                                    <CheckCircleIcon
                                                                        fontSize="small"
                                                                        sx={{ color: 'info.main', ml: 0.5 }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        }
                                                        variant={isUsed ? 'filled' : 'outlined'}
                                                        color={isUsed ? 'info' : 'default'}
                                                        onDelete={() => handleDelete(perm.id, perm.display_name)}
                                                        deleteIcon={<DeleteIcon />}
                                                        onClick={() => handleOpenDialog(perm)}
                                                        icon={isUsed ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                                                        sx={{
                                                            fontWeight: 500,
                                                            cursor: 'pointer',
                                                            fontSize: '0.875rem',
                                                            '& .MuiChip-icon': {
                                                                color: isUsed ? 'white' : 'action.active',
                                                            },
                                                            '&:hover': {
                                                                bgcolor: isUsed ? 'info.dark' : 'action.hover',
                                                                color: isUsed ? 'white' : 'inherit',
                                                            },
                                                        }}
                                                    />
                                                </Tooltip>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                )}

                {/* Permission Dialog */}
                <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
                        {currentPermission ? 'Edit Permission' : 'Add New Permission'}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Display Name *"
                            fullWidth
                            margin="dense"
                            name="display_name"
                            value={permissionForm.display_name}
                            onChange={handleFormChange}
                            required
                            autoFocus
                        />
                        <TextField
                            label="Permission Key *"
                            fullWidth
                            margin="dense"
                            name="name"
                            value={permissionForm.name}
                            onChange={handleFormChange}
                            required
                            helperText={
                                currentPermission
                                    ? 'Used in code. Changing this may break existing role assignments.'
                                    : 'Auto-generated from Display Name. You can edit it before saving.'
                            }
                        />
                        {currentPermission && permissionForm.name !== currentPermission.name && (
                            <MuiAlert severity="warning" icon={<WarningIcon fontSize="inherit" />} sx={{ mt: 1, fontSize: '0.875rem', py: 0.5 }}>
                                ⚠️ Changing this key may break existing code or role permissions.
                            </MuiAlert>
                        )}
                        <TextField
                            label="Description"
                            fullWidth
                            margin="dense"
                            multiline
                            rows={2}
                            name="description"
                            value={permissionForm.description}
                            onChange={handleFormChange}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setOpenDialog(false)} color="inherit">
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disableElevation
                            disabled={saving || !permissionForm.name.trim() || !permissionForm.display_name.trim()}
                        >
                            {saving ? 'Saving…' : currentPermission ? 'Update' : 'Create'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </DashboardLayout>
    );
};

export default PermissionsPage;